import React from 'react'
import { createBrowserRouter, Navigate, redirect } from 'react-router'
import { ApplicationRoutes } from '~/types/RoutingTypes'

import { getAuthStateFromLocalStorage } from './atoms/AuthenticationState'
import { ResourceErrorBoundary } from './components/ResourceErrorBoundary'
import { CallAnalyticsPage } from './pages/CallAnalytics/CallAnalyticsPage'
import { CallDetailsPage, CallsPage } from './pages/Calls/CallsPage'
import { ContactsPage } from './pages/Contacts/ContactsPage'
import { DashboardsPage } from './pages/Dashboards/DashboardsPage'
import { FaxPage } from './pages/Fax/FaxPage'
import { HomePage } from './pages/Home/HomePage'
import { LoginPage, SelectPbxPage } from './pages/Login/LoginPage'
import MessagingPage from './pages/Messaging/MessagingPage'
import { PopupPage } from './pages/Popup/PopupPage'
import { SettingsPage } from './pages/Settings/SettingsPage'
import { VoicemailPage } from './pages/Voicemail/VoicemailPage'
import WebPhonePage from './pages/WebPhone/WebPhonePage'
import { getUserFromToken } from './utils/auth'

const IconsPage = React.lazy(() =>
  import('./pages/Dev/Icons').then((module) => ({ default: module.IconsPage })),
)

// Create a loading component for Suspense fallback
const LoadingComponent = () => <div>Loading content...</div>

// Auth check loader helper
const authCheckLoader = async () => {
  const authState = getAuthStateFromLocalStorage()
  const token = authState.appSessionJwts?.access_token || undefined

  const user = getUserFromToken(token)
  const isUserLoggedIn = !!user
  const userStateReady = !!user

  const shouldRedirectToLogin = !isUserLoggedIn || !userStateReady
  if (shouldRedirectToLogin) {
    return redirect(ApplicationRoutes.LOGIN)
  }
  return authState
}

// Define the router
export const router = createBrowserRouter([
  {
    path: '/',
    loader: authCheckLoader,
    Component: HomePage,
    errorElement: <ResourceErrorBoundary />,
    children: [
      {
        index: true,
        Component: CallsPage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'calls/*',
        Component: CallsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':callId',
            Component: CallDetailsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'voicemails/*',
        Component: VoicemailPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':voicemailId',
            Component: VoicemailPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'messaging/*',
        Component: MessagingPage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'faxes/*',
        Component: FaxPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':faxId',
            Component: FaxPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'contacts/*',
        Component: ContactsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':contactId',
            Component: ContactsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'call-analytics/*',
        Component: CallAnalyticsPage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'web-phone/*',
        Component: WebPhonePage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'dashboards/*',
        Component: DashboardsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':slug',
            Component: DashboardsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'settings/*',
        Component: SettingsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':setting',
            Component: SettingsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    Component: LoginPage,
  },
  {
    path: '/select-pbx',
    Component: SelectPbxPage,
  },
  {
    path: '/icons',
    element: (
      <React.Suspense fallback={<LoadingComponent />}>
        <IconsPage />
      </React.Suspense>
    ),
  },
  {
    path: '/popup',
    Component: PopupPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
